import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import {
  BillContainer,
  BillEtaLabel,
  BillEtaValue,
  BillFutureOrderHead
} from 'routes/App/ordersV2/OrdersList/styles';
import DisplayField from 'components/fields/DisplayField';
import {Body1, Caption1, Caption2} from 'components/typography';
import {BillDottedLine} from 'routes/App/ordersV2/OrderDetails/styles';
import {FlexContainer, FlexItem} from 'components/layout';
import SubTitle from 'components/SubTitle/styles';
import {
  ORDER,
  ORDER_TYPES,
  ORDER_TYPE,
  ORDER_SOURCE,
  DELIVERY_PROVIDER_TYPE,
  ORDER_CHANNELS
} from 'helpers/constants/orders_v2';
import {groupDigital} from 'helpers/functionUtils/groupByDigital';
import {phoneFormatForView} from 'helpers/functionUtils/helpFunctions';
import {ValueTitle} from 'routes/App/ordersV2/components/Bill/styles';
import {checkIsFutureOrder} from 'helpers/functionUtils/ordersUtils';
import {
  checkIsBaDPOS,
  getBillablePriceAdjustmentsForTotals,
  getTotalItemsLabel,
  shouldShowOrderPrice,
  shouldShowProviderId,
  shouldShowTipsAndTotal,
  shouldShowOrderTotal,
  isSauceDispatchOrder
} from 'helpers/functionUtils/ordersUtilsV2';
import OrderSourceIcon from 'routes/App/ordersV2/components/OrderSourceIcon';

const mapOrderOptionsHelper = (
  item,
  {isModifier = false, quantity = 1} = {},
  useSalesPackage,
  orderSource
) => {
  const lineTotalPrice =
    Number(item.baseUnitPrice || 0) * Number(item.quantity || 0);
  const shouldShowQuantity = !isModifier || Number(item.quantity) > 1;

  return (
    <>
      <Caption1 className='block '>
        {item.sectionName || item?.modifierGroup?.name}
      </Caption1>
      <FlexContainer justifyContent='space-between'>
        <FlexItem className='wordWrap'>
          <Body1>
            {shouldShowQuantity && <b>{`${item.quantity} x `}</b>}
            {item.name}
          </Body1>
        </FlexItem>
        <FlexItem
          style={{
            width: '120px',
            paddingRight: '40px',
            paddingLeft: '10px',
            textAlign: 'right',
            alignSelf: 'self-end'
          }}
        >
          <Body1>
            {' '}
            {shouldShowOrderPrice(
              lineTotalPrice,
              orderSource,
              useSalesPackage
            ) && `$${groupDigital(Number(lineTotalPrice || 0).toFixed(2))}`}
          </Body1>
        </FlexItem>
      </FlexContainer>
      {Array.isArray(item.modifiers) && item.modifiers.length > 0 && (
        <div className='marginLeft2l'>
          {item.modifiers.map(
            (option) =>
              option &&
              mapOrderOptionsHelper(
                option,
                {
                  isModifier: true,
                  quantity: item.quantity * quantity
                },
                useSalesPackage,
                orderSource
              )
          )}
        </div>
      )}
      <div style={{paddingRight: '120px'}}>
        <Body1 className='wordWrap'>
          <i>{item.specialNote && `(${item.specialNote})`}</i>
        </Body1>
      </div>
    </>
  );
};

const deepCount = (item) => {
  let total = Number(item.total || 0);

  if (Array.isArray(item.modifiers) && item.modifiers.length > 0) {
    item.modifiers.forEach((option) => {
      if (option) {
        total += (item.quantity || 0) * deepCount(option);
      }
    });
  }

  return total;
};

export default class Bill extends Component {
  render() {
    const {
      order,
      locationName,
      useSalesPackage,
      timezone,
      allowedServiceFeatures,
      showOtterBrandName
    } = this.props;
    const billableAdjustments = getBillablePriceAdjustmentsForTotals(order);
    const showTipsAndTotal = shouldShowTipsAndTotal(
      order,
      allowedServiceFeatures
    );
    const showOrderTotal = shouldShowOrderTotal(order, allowedServiceFeatures);

    return (
      <BillContainer>
        {checkIsFutureOrder(order) &&
          order[ORDER.ORDER_STATUS] !== 'Canceled' && (
            <BillFutureOrderHead className='marginBottomXs'>
              ***FUTURE ORDER***
            </BillFutureOrderHead>
          )}
        {order[ORDER.ORDER_STATUS] === 'Canceled' && (
          <BillFutureOrderHead className='marginBottomXs CanceledOrder'>
            ***CANCELED ORDER***
          </BillFutureOrderHead>
        )}
        <DisplayField
          label={<BillEtaLabel>ETA</BillEtaLabel>}
          value={
            <BillEtaValue>
              For{' '}
              {moment(`${order[ORDER.DATE]}Z`)
                .utcOffset(0)
                .format('MMMM D, YYYY')}
              ,{' '}
              <span>
                {moment(`${order[ORDER.DATE]}Z`).utcOffset(0).format('h:mm A')}
              </span>
            </BillEtaValue>
          }
          className='marginBottomXs widthAuto'
          spaceBetween
          flexItemFullWidth
        />

        {order[ORDER.AHEAD_TIME] && (
          <>
            <BillDottedLine className='marginBottomSm withoutOpacity' />
            <DisplayField
              label={<Caption1>Placed at</Caption1>}
              value={
                <Caption1>
                  {moment(`${order[ORDER.CREATED_AT]}`)
                    .utcOffset(Number(timezone) || 0)
                    .format('MMM D. h:mm A')}
                </Caption1>
              }
              className='marginBottomXs'
              spaceBetween
            />
            <BillDottedLine className='marginBottomSm withoutOpacity' />
          </>
        )}

        <DisplayField
          label={<Body1>Order Type:</Body1>}
          value={
            <div className='typeWithIcon'>
              <OrderSourceIcon order={order} />
              {(() => {
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.DISPATCH
                ) {
                  return <Caption1>Sauce delivery</Caption1>;
                }
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.LOCATION
                ) {
                  return <Caption1>Self-delivery</Caption1>;
                }
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
                  order[ORDER.SOURCE] === ORDER_SOURCE.UBER
                ) {
                  return <Caption1>Uber delivery</Caption1>;
                }
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
                  order[ORDER.SOURCE] === ORDER_SOURCE.DOORDASH
                ) {
                  return <Caption1>DoorDash delivery</Caption1>;
                }
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
                  order[ORDER.SOURCE] === ORDER_SOURCE.GRUBHUB
                ) {
                  return <Caption1>GrubHub delivery</Caption1>;
                }
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
                  order[ORDER.SOURCE] === ORDER_SOURCE.SLICE
                ) {
                  return <Caption1>Slice delivery</Caption1>;
                }
                if (
                  order[ORDER.ORDER_TYPE] === ORDER_TYPE.DELIVERY &&
                  order[ORDER.DELIVERY]?.provider ===
                    DELIVERY_PROVIDER_TYPE.ORDER_PROVIDER &&
                  order[ORDER.SOURCE] === ORDER_SOURCE.EZCATER
                ) {
                  return <Caption1>EZCater delivery</Caption1>;
                }
                if (order[ORDER.ORDER_TYPE] === ORDER_TYPE.DINE_IN) {
                  return <Caption1>Dine in</Caption1>;
                }
                return <Caption1>Pickup</Caption1>;
              })()}
            </div>
          }
          className='marginBottomXs ordersV2BillType'
          spaceBetween
        />
        {showOtterBrandName && order[ORDER.LOCATION]?.storeName && (
          <DisplayField
            label={<Body1>Location:</Body1>}
            value={<Caption1>{order[ORDER.LOCATION].storeName}</Caption1>}
            className='marginBottomXs'
            spaceBetween
          />
        )}
        {ORDER_TYPES[order[ORDER.ORDER_TYPE]] ===
          ORDER_TYPES[ORDER_TYPE.DELIVERY] &&
          order[ORDER.DELIVERY]?.address?.formattedAddress && (
            <>
              <DisplayField
                label={<Body1>Delivery Address:</Body1>}
                value={
                  <Caption1>
                    {order[ORDER.DELIVERY]?.address?.formattedAddress}
                  </Caption1>
                }
                className='marginBottomXs'
                spaceBetween
              />
              {order[ORDER.DELIVERY]?.provider ===
                DELIVERY_PROVIDER_TYPE.DISPATCH &&
                typeof order[ORDER.DELIVERY]?.address?.notes === 'string' &&
                order[ORDER.DELIVERY]?.address?.notes?.length > 0 && (
                  <DisplayField
                    label={<Body1>Delivery Notes:</Body1>}
                    value={
                      <Caption1>
                        {order[ORDER.DELIVERY]?.address?.notes}
                      </Caption1>
                    }
                    className='marginBottomXs'
                    spaceBetween
                  />
                )}
            </>
          )}

        <BillDottedLine className='marginBottomSm withoutOpacity' />

        {typeof order[ORDER.INVOICE_ID] === 'string' && (
          <DisplayField
            label={<Body1>Order ID:</Body1>}
            value={
              <Caption1 className='wordWrap'>
                {order[ORDER.INVOICE_ID]}
              </Caption1>
            }
            className='marginBottomXs'
            flexWrap
            flexItemFullWidth
            spaceBetween
          />
        )}
        {shouldShowProviderId(order[ORDER.EXTERNAL_ID]) && (
          <DisplayField
            label={<Body1>Provider ID:</Body1>}
            value={
              <Caption1 className='wordWrap'>
                {order[ORDER.EXTERNAL_ID]}
              </Caption1>
            }
            className='marginBottomXs'
            flexWrap
            flexItemFullWidth
            spaceBetween
          />
        )}

        <BillDottedLine className='marginBottomXl withoutOpacity' />

        <SubTitle className='marginBottomMd wordWrap'>
          {order[ORDER.CUSTOMER]?.fullName || ''}
        </SubTitle>
        {order[ORDER.CUSTOMER]?.phone && (
          <DisplayField
            label={<Body1>Phone Number:</Body1>}
            value={
              <Caption1>
                {phoneFormatForView(order[ORDER.CUSTOMER]?.phone)}
              </Caption1>
            }
            className='marginBottomXs'
            spaceBetween
          />
        )}
        {order[ORDER.CUSTOMER]?.phoneCode && (
          <DisplayField
            label={<Body1>Phone Code:</Body1>}
            value={<Caption1>{order[ORDER.CUSTOMER]?.phoneCode}</Caption1>}
            className='marginBottomXs'
            spaceBetween
          />
        )}
        {order[ORDER.CUSTOMER]?.email && (
          <DisplayField
            label={<Body1>Email Address:</Body1>}
            value={
              <Caption1 className='wordWrap'>
                {order[ORDER.CUSTOMER]?.email}
              </Caption1>
            }
            className='marginBottomXs'
            flexWrap
            flexItemFullWidth
            spaceBetween
          />
        )}

        <DisplayField
          label={<Body1>Location:</Body1>}
          value={<Caption1>{locationName}</Caption1>}
          className='marginBottomXl'
          spaceBetween
        />

        <BillDottedLine className='marginBottomXl' />

        <SubTitle className='marginBottomMd wordWrap'>
          {getTotalItemsLabel(order[ORDER.CART]?.items)}
        </SubTitle>

        <BillDottedLine className='marginBottomXl' />

        {order[ORDER.CART]?.items?.map((item) => (
          <>
            {mapOrderOptionsHelper(
              item,
              {},
              useSalesPackage,
              order[ORDER.SOURCE]
            )}
            <div style={{position: 'relative', height: '20px'}}>
              <Caption1
                style={{
                  display: 'block',
                  position: 'absolute',
                  top: '0px',
                  right: '0'
                }}
              >
                {shouldShowOrderPrice(
                  item.total,
                  order[ORDER.SOURCE],
                  useSalesPackage
                ) && `$${groupDigital(Number(item.total || 0).toFixed(2))}`}
              </Caption1>
            </div>
          </>
        ))}

        <BillDottedLine className='marginTopXl marginBottomXl' />

        {order[ORDER.CART]?.specialNote && (
          <>
            <DisplayField
              label={<Body1>Note:</Body1>}
              value={<Caption1>{order[ORDER.CART]?.specialNote}</Caption1>}
              className='marginBottomXs'
              spaceBetween
            />
            <BillDottedLine className='marginBottomXl' />
          </>
        )}

        {!useSalesPackage && (
          <>
            {shouldShowOrderPrice(
              order[ORDER.TOTALS]?.locationSubtotal,
              order[ORDER.SOURCE],
              useSalesPackage
            ) && (
              <DisplayField
                label={<Caption1>Subtotal:</Caption1>}
                value={
                  <Caption1>
                    ${' '}
                    {groupDigital(
                      Number(
                        order[ORDER.TOTALS]?.locationSubtotal || 0
                      ).toFixed(2)
                    )}
                  </Caption1>
                }
                spaceBetween
                className='marginBottomXs'
              />
            )}
            {!!billableAdjustments?.length &&
              billableAdjustments.map((adjustment) => (
                <DisplayField
                  label={<Caption1>Adjustment:</Caption1>}
                  value={
                    <Caption1>
                      ${' '}
                      {groupDigital(
                        Number(adjustment.sourceAmount || 0).toFixed(2)
                      )}
                    </Caption1>
                  }
                  spaceBetween
                  className='marginBottomXs'
                />
              ))}
            {!!Number(order[ORDER.TAXES]?.salesTaxAmount) && (
              <DisplayField
                label={<Caption1>Tax:</Caption1>}
                value={
                  <Caption1>
                    ${' '}
                    {groupDigital(
                      Number(order[ORDER.TAXES]?.salesTaxAmount || 0).toFixed(2)
                    )}
                  </Caption1>
                }
                spaceBetween
                className='marginBottomXs'
              />
            )}
            {!!Number(order[ORDER.TAXES]?.alcoholTaxAmount) && (
              <DisplayField
                label={<Caption1>Alcohol Tax:</Caption1>}
                value={
                  <Caption1>
                    ${' '}
                    {groupDigital(
                      Number(order[ORDER.TAXES]?.alcoholTaxAmount).toFixed(2)
                    )}
                  </Caption1>
                }
                spaceBetween
                className='marginBottomXs'
              />
            )}
            {!!Number(
              order[ORDER.FEES]?.locationOptionalServiceChargeAmount
            ) && (
              <DisplayField
                label={<Caption1>Service Charge:</Caption1>}
                value={
                  <Caption1>
                    ${' '}
                    {groupDigital(
                      Number(
                        order[ORDER.FEES]?.locationOptionalServiceChargeAmount
                      ).toFixed(2)
                    )}
                  </Caption1>
                }
                spaceBetween
                className='marginBottomXs'
              />
            )}

            {!order[ORDER.DELIVERY]?.isDispatch && (
              <>
                {Number(order[ORDER.FEES]?.deliveryFee) ? (
                  <DisplayField
                    label={<Caption1>Delivery:</Caption1>}
                    value={
                      <Caption1>
                        ${' '}
                        {groupDigital(
                          Number(order[ORDER.FEES]?.deliveryFee || 0).toFixed(2)
                        )}
                      </Caption1>
                    }
                    className='marginBottomXs'
                    spaceBetween
                  />
                ) : null}
              </>
            )}
            {showTipsAndTotal &&
              Number(order[ORDER.TIPS_AMOUNT]) > 0 &&
              order[ORDER.ORDER_TYPE] === ORDER_TYPE.PICKUP && (
                <DisplayField
                  label={<Caption1>Tip:</Caption1>}
                  value={
                    <Caption1>
                      ${' '}
                      {groupDigital(
                        Number(order[ORDER.TIPS_AMOUNT] || 0).toFixed(2)
                      )}
                    </Caption1>
                  }
                  className='marginBottomXs'
                  spaceBetween
                />
              )}
            {showTipsAndTotal &&
              Number(order[ORDER.TIPS_AMOUNT]) > 0 &&
              order[ORDER.ORDER_TYPE] !== ORDER_TYPE.PICKUP &&
              !isSauceDispatchOrder(order) && (
                <DisplayField
                  label={<Caption1>Driver tip:</Caption1>}
                  value={
                    <Caption1>
                      ${' '}
                      {groupDigital(
                        Number(order[ORDER.TIPS_AMOUNT] || 0).toFixed(2)
                      )}
                    </Caption1>
                  }
                  className='marginBottomXs'
                  spaceBetween
                />
              )}
            {order[ORDER.DISCOUNTS]?.length > 0 &&
              order[ORDER.DISCOUNTS]?.map((discount) => (
                <DisplayField
                  label={
                    <ValueTitle className='wordWrap'>
                      Discount ({discount.name}):
                    </ValueTitle>
                  }
                  value={
                    <Caption1>
                      - ${' '}
                      {groupDigital(
                        Number(discount.locationAmount || 0).toFixed(2)
                      )}
                    </Caption1>
                  }
                  className='marginBottomXs'
                  spaceBetween
                />
              ))}
            <br />
            {showOrderTotal &&
              shouldShowOrderPrice(
                order[ORDER.TOTALS]?.locationTotal,
                order[ORDER.SOURCE],
                useSalesPackage
              ) && (
                <DisplayField
                  label={<SubTitle>Total:</SubTitle>}
                  value={
                    <Caption1>
                      ${' '}
                      {groupDigital(
                        Number(order[ORDER.TOTALS]?.locationTotal || 0).toFixed(
                          2
                        )
                      )}
                    </Caption1>
                  }
                  spaceBetween
                />
              )}
            <BillDottedLine className='marginTopXl marginBottomSm' />
          </>
        )}
        {order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE &&
          order[ORDER.CHANNEL] !== ORDER_CHANNELS.STREAM_EVENT &&
          order[ORDER.CHANNEL] !== ORDER_CHANNELS.SPOTON && (
            <Caption1 className='textAlignCenter '>
              Thank you for your order. <br />
              Order directly with us and save up to 20-40% on every order.
            </Caption1>
          )}
      </BillContainer>
    );
  }
}

Bill.defaultProps = {
  useSalesPackage: false,
  showOtterBrandName: false
};

Bill.propTypes = {
  order: PropTypes.object.isRequired,
  locationName: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  useSalesPackage: PropTypes.bool,
  allowedServiceFeatures: PropTypes.array.isRequired,
  showOtterBrandName: PropTypes.bool
};
